/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
// ════════════════════════════════════════════════════════════════
// PainLocationScreen.jsx (Conversational)
// Body diagram with tappable zone buttons + canvas highlight overlay.
// Ports prototype's showPainLocation(), toggleZone(), drawHighlights().
// Converted: pushes Myra message on mount, returns inlineUi only,
// pushes text-only frozen widget + user echo on completion.
// ════════════════════════════════════════════════════════════════

import React, { useState, useCallback, useEffect, useRef } from "react";
import { useScreenVoice } from "./useScreenVoice";
import { ZONE_COORDS, SKELETON, hlType, splitHalf, edgeSide, hasLegPain } from "./GSA_CONSTANTS";
import { MYRA_MESSAGES } from "./GSA_MYRA_MESSAGES";
import styles from "./SelfAssessment.module.css";
import MyraChatService from "../voice-agent/MyraChatService";
import MyraInteraction from "../self-assesment/MyraInteractionController";
import VoiceService from "../voice-agent/VH-Myra-FrontEnd";

let _plId = 90000;
function nextPlId() {
  return ++_plId;
}

export default function PainLocationScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue, readOnly = false, entry }) {
  const problemArea = assessmentData.problemArea;
  const coords = ZONE_COORDS[problemArea];
  const leftZones = coords ? Object.keys(coords.left) : [];
  const rightZones = coords ? Object.keys(coords.right) : [];

  const [selectedZones, setSelectedZones] = useState(new Set());
  const [ready, setReady] = useState(false);
  const [locked, setLocked] = useState(false);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const wrapRef = useRef(null);
  const isVoiceMode = MyraInteraction.getMode() === "voice";

  // ── Push divider + Myra message on mount ──
  useEffect(() => {
    let cancelled = false;
    async function init() {
      if (!readOnly) {
        await showMyraMessage(MYRA_MESSAGES.painLocation?.prompt || "Tap the buttons on both sides of the body to highlight where you're feeling pain.", 800);
      }
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (readOnly && entry?.data?.selectedZones) {
      setSelectedZones(new Set(entry.data.selectedZones));
      setReady(true); // skip loader
    }
  }, [readOnly, entry]);

  // ── Voice ──
  const validateAndContinue = useCallback(() => {
    if (selectedZones.size === 0) {
      if (isVoiceMode && VoiceService) {
        VoiceService.doSay("Please select at least one area where you feel pain before we continue.");
      }
      return;
    }
    handleContinue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZones]);

  useScreenVoice({
    screenName: "pain_location",
    flowName: "flow_gsa",
    payloadKey: "pain_location",
    commands: {
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (value === "continue_command") {
          validateAndContinue();
          return;
        }
        if (extracted && extracted.selectedZones) {
          console.log("[VOICE] Updating selected zones to:", extracted.selectedZones);

          const zonesArray = Array.isArray(extracted.selectedZones) ? extracted.selectedZones : [extracted.selectedZones];

          // Convert array to Set
          const zonesSet = new Set(zonesArray);
          setSelectedZones(zonesSet);
        }
      },
      MOVE_ON: () => validateAndContinue(),
    },
    exitReason: "pain_location_completed",
  });

  // ── Canvas drawing ──
  const drawHighlights = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !img || !wrap || !coords) return;

    const wrapRect = wrap.getBoundingClientRect();
    canvas.width = wrapRect.width;
    canvas.height = wrapRect.height;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const imgRect = img.getBoundingClientRect();
    const offX = imgRect.left - wrapRect.left;
    const offY = imgRect.top - wrapRect.top;
    const imgW = imgRect.width;
    const imgH = imgRect.height;

    selectedZones.forEach((key) => {
      const [side, zone] = key.split(":");
      const c = coords[side]?.[zone];
      if (!c) return;

      const x = offX + (c[0] / 100) * imgW;
      const y = offY + (c[1] / 100) * imgH;
      const w = (c[2] / 100) * imgW;
      const h = (c[3] / 100) * imgH;
      const type = hlType(zone);

      if (type === "full") {
        ctx.fillStyle = "rgba(220,38,38,0.35)";
        roundRect(ctx, x, y, w, h, Math.min(6, w / 2, h / 2));
        ctx.fill();
      } else if (type === "split") {
        ctx.fillStyle = "rgba(220,38,38,0.35)";
        const half = splitHalf(zone);
        const drawY = half === "upper" ? y : y + h / 2;
        const drawH = h / 2;
        roundRect(ctx, x, drawY, w, drawH, Math.min(4, w / 2, drawH / 2));
        ctx.fill();
      } else if (type === "edge") {
        ctx.strokeStyle = "rgba(220,38,38,0.70)";
        ctx.lineWidth = 3.5;
        ctx.lineCap = "round";
        const eSide = edgeSide(zone);
        let lineX;
        if (side === "left") {
          lineX = eSide === "outer" ? x : x + w;
        } else {
          lineX = eSide === "outer" ? x + w : x;
        }
        ctx.beginPath();
        ctx.moveTo(lineX, y);
        ctx.lineTo(lineX, y + h);
        ctx.stroke();
      }
    });
  }, [selectedZones, coords]);

  // Redraw on selection change
  useEffect(() => {
    drawHighlights();
  }, [drawHighlights]);

  // ResizeObserver for canvas redraw
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !window.ResizeObserver) return;
    const ro = new ResizeObserver(() => drawHighlights());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [drawHighlights]);

  // ── Zone toggle ──
  const toggleZone = useCallback((side, zone) => {
    setSelectedZones((prev) => {
      const next = new Set(prev);
      const key = `${side}:${zone}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ── Continue ──
  async function handleContinue() {
    if (selectedZones.size === 0 || readOnly || locked) return;
    setLocked(true);
    const zonesArray = [...selectedZones];
    const isLegPain = hasLegPain(zonesArray);

    // Build user echo text (matching prototype)
    const count = zonesArray.length;
    const zoneLabels = zonesArray.map((k) => {
      const [side, zone] = k.split(":");
      return `${side === "left" ? "Left" : "Right"} ${zone}`;
    });
    const echoText = count <= 3 ? zoneLabels.join(", ") : `${count} areas selected`;

    // Push text-only frozen widget + user echo (canvas too complex to freeze)
    pushToHistory(
      {
        type: "pain-location-summary",
        id: nextPlId(),
        data: {
          selectedZones: zonesArray,
          problemArea: problemArea,
          zoneText: echoText,
        },
      },
      { type: "user", text: echoText, id: nextPlId() },
    );
    // pushToHistory({ type: "frozen-widget", widgetType: "pain_location_text", id: nextPlId(), data: { zoneText: echoText } }, { type: "user", text: echoText, id: nextPlId() });

    // Myra transition message (matching prototype)
    const transitionMsg = count <= 3 ? `${zoneLabels.join(", ")} — got it. Now I need to do a quick safety check before we continue.` : `Got it — ${count} areas noted. Now I need to do a quick safety check before we continue.`;
    await showMyraMessage(transitionMsg, 800);

    onContinue({
      selectedZones: zonesArray,
      painLocationComplete: true,
      isLegPain,
    });
  }

  // ── No coords fallback ──
  if (!coords) {
    return (
      <div className={styles.inlineUi}>
        <p>No zone data for this body area.</p>
        <button className={styles.btnSafe} onClick={() => onContinue({ selectedZones: [], painLocationComplete: true, isLegPain: false })}>
          Continue
        </button>
      </div>
    );
  }

  if (!ready) return null;

  const canContinue = selectedZones.size > 0;

  return (
    <div className={styles.inlineUi}>
      {!locked ? (
        <>
          <div className={styles.painLocationWrap}>
            {/* Side labels */}
            <div className={styles.sideLabels}>
              <span className={styles.sideLabel}>LEFT</span>
              <span className={styles.sideLabelSpacer} />
              <span className={styles.sideLabel}>RIGHT</span>
            </div>

            {/* Body section: left buttons | image+canvas | right buttons */}
            <div className={styles.bodySection}>
              <div className={styles.btnCol}>
                {leftZones.map((z) => {
                  const key = `left:${z}`;
                  const isActive = selectedZones.has(key);
                  return (
                    <button key={key} className={`${styles.zoneBtn} ${isActive ? styles.active : ""}`} onClick={() => !readOnly && toggleZone("left", z)}>
                      {z}
                    </button>
                  );
                })}
              </div>

              <div className={styles.bodyWrap} ref={wrapRef}>
                <img ref={imgRef} src={SKELETON} alt="Body outline" onLoad={drawHighlights} />
                <canvas ref={canvasRef} className={styles.hlCanvas} />
              </div>

              <div className={styles.btnCol}>
                {rightZones.map((z) => {
                  const key = `right:${z}`;
                  const isActive = selectedZones.has(key);
                  return (
                    <button key={key} className={`${styles.zoneBtn} ${isActive ? styles.active : ""}`} onClick={() => !readOnly && toggleZone("right", z)}>
                      {z}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {!readOnly && (
            <button className={styles.btnSafe} disabled={!canContinue} onClick={handleContinue} style={{ opacity: canContinue ? 1 : 0.5 }}>
              Continue
            </button>
          )}
        </>
      ) : (
        <div></div>
      )}
    </div>
  );
}

// ── Canvas helper ──
function roundRect(ctx, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
